import { Fragment, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  fetchCrossSectorValuation,
  fetchFilterableFields,
  fetchIndexCapeDetail,
  fetchIndexValuationDetail,
  fetchIndices,
  type CapeDto,
  type ConditionGroup,
  type CrossSectorValuationResponseDto,
  type CrossSectorValuationRowDto,
  type FilterableFieldDto,
  type IndexRowDto,
  type IndexValuationDto,
  type IndicesFilterResponse,
  type SavedFilterDto,
} from "../api";
import { createSavedFilter, deleteSavedFilter, getDefaultView, listSavedFilters, setDefaultView } from "../api";
import { ColumnsMenu } from "../components/ColumnsMenu";
import { FilterBuilder } from "../components/FilterBuilder";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { AsOfBadge } from "../components/AsOfBadge";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";
import { buildIndexColumns, type ColumnDef } from "../fields";
import { ValuationBandChart } from "../components/ValuationBandChart";
import { CapeChart } from "../components/CapeChart";

const DEFAULT_GROUPS: ConditionGroup[] = [{ op: "AND", conditions: [] }];
const TABLE_CONTEXT = "indices";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const REGIME_LABEL: Record<IndexRowDto["sigma_regime_status"], string> = {
  ADEQUATE: "adequate",
  DEGRADED: "degraded",
  REGIME_BREAK: "regime break",
  NO_DATA: "no data",
};

function SigmaCell({ row }: { row: IndexRowDto }) {
  return (
    <span className="signal-cell">
      <span className="signal-value">{row.predicted_sigma !== null ? `${(row.predicted_sigma * 100).toFixed(1)}%` : "—"}</span>
      <span
        className={`regime-badge sm ${row.sigma_regime_status.toLowerCase()}`}
        title={row.sigma_regime_as_of ? `regime read as of ${row.sigma_regime_as_of}` : undefined}
      >
        {REGIME_LABEL[row.sigma_regime_status]}
      </span>
      <span className="gaps-badge" title={row.no_directional_signal_note}>
        no direction
      </span>
    </span>
  );
}

/** Shared between the screener's Valuation column and the cross-sector
 * tab -- one rendering for "cheap/fair/expensive vs own history" +
 * "...vs NIFTY", never two independently-drifting versions. */
function ValuationChips({ v, showVsNifty = true }: { v: IndexValuationDto; showVsNifty?: boolean }) {
  if (!v.available) {
    return <span className="mut" title={v.note}>—</span>;
  }
  return (
    <span className="signal-cell" title={v.note}>
      <span className="signal-value">{v.pe !== null ? `${v.pe.toFixed(1)}×` : "—"}</span>
      {v.classification && <span className={`valuation-chip ${v.classification}`}>{v.classification}</span>}
      {showVsNifty && v.vs_nifty_classification && (
        <span className={`valuation-chip outline ${v.vs_nifty_classification}`} title="vs NIFTY's own P/E, same date">
          vs NIFTY: {v.vs_nifty_classification}
        </span>
      )}
    </span>
  );
}

function ValuationCell({ row }: { row: IndexRowDto }) {
  return <ValuationChips v={row.valuation} showVsNifty={row.security_id !== "IDX:NIFTY"} />;
}

function cellValue(row: IndexRowDto, key: string): unknown {
  if (key === "display_ticker" || key === "close" || key === "day_change_pct" || key === "security_id") {
    return (row as unknown as Record<string, unknown>)[key];
  }
  if (key === "sigma") return row.predicted_sigma;
  if (key === "constituent_breadth") return row.constituent_breadth_pct;
  if (key === "valuation") return row.valuation.pe;
  return row.features[key] ?? null;
}

function renderCell(row: IndexRowDto, column: ColumnDef, onSelectIndex: (securityId: string) => void) {
  if (column.key === "sigma") return <SigmaCell row={row} />;
  if (column.key === "display_ticker") {
    return (
      <span className="tk tk-link" onClick={() => onSelectIndex(row.security_id)} title="Open instrument detail">
        {row.display_ticker ?? row.security_id}
      </span>
    );
  }
  if (column.key === "close") {
    if (row.close === null) return "—";
    return (
      <span title={row.bar_date ? `as of ${row.bar_date}` : undefined}>
        {row.close.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
      </span>
    );
  }
  if (column.key === "day_change_pct") {
    const v = row.day_change_pct;
    return v !== null ? <span className={v >= 0 ? "pos" : "neg"}>{v >= 0 ? "+" : ""}{v.toFixed(2)}%</span> : "—";
  }
  if (column.key === "valuation") return <ValuationCell row={row} />;
  if (column.key === "constituent_breadth") {
    const v = row.constituent_breadth_pct;
    if (v === null) return <span className="mut" title="no constituent mapping for this index">—</span>;
    return (
      <span className={v >= 50 ? "pos" : "neg"} title={`${row.constituent_count} constituent stock(s)`}>
        {v.toFixed(0)}% {v >= 50 ? "▲" : "▼"}
      </span>
    );
  }
  if (column.key === "fii_net" || column.key === "dii_net") {
    const v = row.features[column.key];
    if (v === null || v === undefined) return "—";
    return <span className={v >= 0 ? "pos" : "neg"}>{v >= 0 ? "+" : ""}₹{v.toLocaleString("en-IN")} Cr</span>;
  }
  const v = row.features[column.key];
  return v !== null && v !== undefined ? v.toFixed(4) : "—";
}

export function IndicesPage({
  token, onUnauthorized, onSelectIndex,
}: {
  token: string; onUnauthorized: () => void; onSelectIndex: (securityId: string) => void;
}) {
  const [tab, setTab] = useState<"screener" | "valuation">("screener");
  const [mode, setMode] = useState<"live" | "backtest">("live");
  const [asOf, setAsOf] = useState(todayIso());
  const [groups, setGroups] = useState<ConditionGroup[]>(DEFAULT_GROUPS);
  const [groupOp, setGroupOp] = useState<"AND" | "OR">("AND");
  const [fields, setFields] = useState<FilterableFieldDto[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilterDto[]>([]);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(new Set(buildIndexColumns([]).filter((c) => c.defaultVisible).map((c) => c.key)));
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [data, setData] = useState<IndicesFilterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  useEffect(() => {
    listSavedFilters(token)
      .then(setSavedFilters)
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    Promise.all([fetchFilterableFields(token, "index"), getDefaultView(token, TABLE_CONTEXT)])
      .then(([liveFields, view]) => {
        setFields(liveFields);
        if (view.columns && view.columns.length) {
          setVisibleCols(new Set(view.columns));
        } else {
          setVisibleCols(new Set(buildIndexColumns(liveFields).filter((c) => c.defaultVisible).map((c) => c.key)));
        }
        if (view.sort_spec) {
          setSortKey(view.sort_spec.key);
          setSortDir(view.sort_spec.dir);
        }
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const columns = useMemo(() => buildIndexColumns(fields), [fields]);
  const forcedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const g of groups) for (const c of g.conditions) s.add(c.field);
    return s;
  }, [groups]);
  const effectiveVisibleCols = useMemo(() => new Set([...visibleCols, ...forcedKeys]), [visibleCols, forcedKeys]);
  const displayedColumns = columns.filter((c) => effectiveVisibleCols.has(c.key));

  // Same fix as StocksPage.tsx: `fields` loads asynchronously, so the very
  // first fetch (mount) would otherwise run with extra_columns=[] and
  // nothing would re-fetch once fields/visibleCols arrived -- date changes
  // (or anything else) would appear to "not update the columns" because
  // the fetch right before it never asked for them. Including this
  // signature in the effect's deps closes that race.
  const extraColumnsKey = displayedColumns
    .filter((c) => c.isFeature)
    .map((c) => c.key)
    .sort()
    .join(",");

  async function run() {
    setLoading(true);
    setError(null);
    try {
      const extraColumns = displayedColumns.filter((c) => c.isFeature).map((c) => c.key);
      const res = await fetchIndices(token, { groups, group_op: groupOp, extra_columns: extraColumns, limit: 50, offset: 0 }, asOf);
      setData(res);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "request failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf, extraColumnsKey]);

  // Live means "today" -- switching back must not leave a stale backtest
  // date silently governing what "live" shows.
  function switchMode(next: "live" | "backtest") {
    setMode(next);
    if (next === "live") setAsOf(todayIso());
  }

  const rows = useMemo(() => {
    if (!data) return [];
    const copy = [...data.rows];
    if (sortKey) {
      copy.sort((a, b) => {
        const av = cellValue(a, sortKey);
        const bv = cellValue(b, sortKey);
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).localeCompare(String(bv));
      });
      if (sortDir === "desc") copy.reverse();
    }
    return copy;
  }, [data, sortKey, sortDir]);

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function exportCsv() {
    if (!data) return;
    exportRowsAsCsv(
      rows.map((r) => {
        const rec: Record<string, unknown> = {};
        for (const c of displayedColumns) rec[c.key] = cellValue(r, c.key);
        return rec;
      }),
      displayedColumns.map((c) => ({ key: c.key, label: c.label })),
      "indices.csv",
    );
  }

  async function handleSaveStrategy(name: string) {
    try {
      const saved = await createSavedFilter(token, name, { groups, group_op: groupOp, signal_output_type: "return" }, null);
      setSavedFilters((prev) => [saved, ...prev]);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "save strategy failed");
    }
  }
  function handleLoadStrategy(f: SavedFilterDto) {
    setGroups(f.filter_tree.groups);
    setGroupOp(f.filter_tree.group_op);
  }
  async function handleDeleteStrategy(filterId: string) {
    try {
      await deleteSavedFilter(token, filterId);
      setSavedFilters((prev) => prev.filter((f) => f.filter_id !== filterId));
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "delete failed");
    }
  }

  async function handleSaveDefaultView() {
    try {
      await setDefaultView(token, TABLE_CONTEXT, Array.from(visibleCols), sortKey ? { key: sortKey, dir: sortDir } : null);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "save failed");
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Indices</h1>
          <div className="page-sub">
            Screening <b>{data ? data.total_universe : "…"}</b> sectoral &amp; broad indices — the funnel's middle
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div className="asof-control">
            <button
              className={`app-nav-tab${mode === "live" ? " active" : ""}`}
              style={{ padding: "5px 10px", fontSize: "0.75rem" }}
              onClick={() => switchMode("live")}
            >
              Live
            </button>
            <button
              className={`app-nav-tab${mode === "backtest" ? " active" : ""}`}
              style={{ padding: "5px 10px", fontSize: "0.75rem" }}
              onClick={() => switchMode("backtest")}
            >
              Backtest
            </button>
          </div>
          {mode === "live" ? (
            <div className="asof-control">
              <span className="asof-static">as of {todayIso()} · close</span>
            </div>
          ) : (
            <div className="asof-control">
              <label>Rewind to</label>
              <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} max={todayIso()} />
            </div>
          )}
        </div>
      </div>

      <div className="levtabs" style={{ marginBottom: 16 }}>
        <button className={tab === "screener" ? "on" : undefined} onClick={() => setTab("screener")}>Screener</button>
        <button className={tab === "valuation" ? "on" : undefined} onClick={() => setTab("valuation")}>
          Cross-sector valuation
        </button>
      </div>

      {tab === "valuation" ? (
        <CrossSectorValuationTab token={token} asOf={mode === "live" ? todayIso() : asOf}
          onAuthError={handleAuthAwareError} onSelectIndex={onSelectIndex} />
      ) : (
      <>
      {mode === "backtest" && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>◷</span>
          <span>
            Viewing indices as-of-T on {asOf}: real historical close, technicals, and σ. There is no per-index
            directional prediction to score (μ is neutral drift by design) — σ's own live track record lives in
            Evaluation slots, not a per-row backtest column here.
          </span>
        </div>
      )}

      {data && data.data_as_of && data.data_as_of < asOf && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>⚠</span>
          <span>
            No bars/features/predictions exist past <b>{data.data_as_of}</b> yet. Every date from {data.data_as_of}{" "}
            onward — including "Live" — resolves to that same last-available snapshot; this is the honest as-of-T
            fallback, not a stuck filter. Pick a date on or before {data.data_as_of} to see genuinely different
            values.
          </span>
        </div>
      )}

      <PageHelper>
        Screen all 17 indices by real close, day-change, and technicals. Every index carries a real, measured{" "}
        <FieldHelp term="real_magnitude_sigma" />volatility forecast but deliberately <FieldHelp term="no_directional_signal" />
        no directional signal — see the banner below for why.
      </PageHelper>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          Indices carry a real, measured volatility forecast (the persistence convention — shown as a literal %) but{" "}
          <b>no directional signal</b>: 4 pilots found no forecastable index-level return edge, so mu is neutral drift
          by design. No ring, arrow, or up/down badge is shown here — that would be fabricated.
        </span>
      </div>

      <FilterBuilder
        groups={groups}
        groupOp={groupOp}
        fields={fields}
        token={token}
        asOf={asOf}
        savedFilters={savedFilters}
        onGroupsChange={setGroups}
        onGroupOpChange={setGroupOp}
        onAddGroup={() => setGroups([...groups, { op: "AND", conditions: [] }])}
        onRun={run}
        onClear={() => setGroups(DEFAULT_GROUPS)}
        onSaveStrategy={handleSaveStrategy}
        onLoadStrategy={handleLoadStrategy}
        onDeleteStrategy={handleDeleteStrategy}
        running={loading}
      />

      <section className="results-panel">
        <div className="results-bar">
          <div className="results-left">
            <span className="results-count">
              <b>{data ? data.matched : "…"}</b> matches
            </span>
            {data && <AsOfBadge date={data.data_as_of} reference={data.expected_as_of} label="bars/features" />}
          </div>
          <div className="results-tools">
            <ColumnsMenu columns={columns} visible={visibleCols} forcedKeys={forcedKeys} onChange={setVisibleCols} onSaveDefault={handleSaveDefaultView} />
            <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
          </div>
        </div>

        {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

        {!error && data && data.matched === 0 && (
          <div className="empty-state">
            <div className="headline">No indices match on {asOf}</div>
            <div className="coverage-note">{data.coverage_report}</div>
          </div>
        )}

        {!error && data && data.matched > 0 && (
          <div className="table-wrap">
            <table className="stocks-table">
              <thead>
                <tr>
                  {displayedColumns.map((c) => (
                    <th
                      key={c.key}
                      className={c.key === "display_ticker" ? "l" : c.key === "sigma" || c.key === "valuation" ? "c" : undefined}
                      onClick={() => toggleSort(c.key)}
                    >
                      {c.label} {c.key === "sigma" && <FieldHelp term="real_magnitude_sigma" />} {c.key === "valuation" && <FieldHelp term="nifty_valuation_band" />} {(c.key === "fii_net" || c.key === "dii_net") && <FieldHelp term="fii_dii_flow" />} {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.security_id}>
                    {displayedColumns.map((c) => (
                      <td key={c.key} className={c.key === "display_ticker" ? "l" : c.key === "sigma" || c.key === "valuation" ? "c" : "num"}>
                        {renderCell(row, c, onSelectIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="foot-note">
          Vol σ is the real, monitored persistence-convention estimate (data_spec §10.9). "no direction" is shown on
          every row deliberately — see the banner above for why. Technical columns are stored for NIFTY and SENSEX
          (Phase 3's feature backfill); every other index computes the same definitions on demand from its own raw
          bars — real values either way, never zeros for missing history.
        </div>
      </section>
      </>
      )}
    </>
  );
}

// ---- CROSS-SECTOR VALUATION ----

function CrossSectorValuationTab({
  token, asOf, onAuthError, onSelectIndex,
}: {
  token: string; asOf: string; onAuthError: (err: unknown) => boolean;
  onSelectIndex: (securityId: string) => void;
}) {
  const [data, setData] = useState<CrossSectorValuationResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    fetchCrossSectorValuation(token, asOf)
      .then(setData)
      .catch((err) => { if (!onAuthError(err)) setError(err instanceof ApiError ? err.message : "failed to load valuation"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, asOf]);

  const { sorted, sortKey, sortDir, toggleSort } = useSort<CrossSectorValuationRowDto>(
    data?.rows ?? [],
    (row, key) => {
      if (key === "display_ticker") return row.display_ticker;
      if (key === "pe") return row.valuation.pe;
      if (key === "classification") return row.valuation.classification;
      if (key === "pe_relative_to_nifty") return row.valuation.pe_relative_to_nifty;
      if (key === "vs_nifty_classification") return row.valuation.vs_nifty_classification;
      return null;
    },
  );

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!data) return <div className="empty-state">Loading…</div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="h3" style={{ padding: "16px 16px 0" }}>
        <span>
          Cross-sector valuation <FieldHelp term="nifty_valuation_band" />
          <span className="note">every index's P/E vs its own history and vs NIFTY — sector rotation at a glance</span>
        </span>
        <AsOfBadge date={data.as_of} reference={data.expected_as_of} />
      </div>
      <div className="table-wrap">
        <table className="stocks-table">
          <thead>
            <tr>
              <th className="l clickable" onClick={() => toggleSort("display_ticker")}>Index {sortGlyph("display_ticker", sortKey, sortDir)}</th>
              <th className="r clickable" onClick={() => toggleSort("pe")}>P/E {sortGlyph("pe", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("classification")}>
                vs own history {sortGlyph("classification", sortKey, sortDir)}
              </th>
              <th className="r clickable" onClick={() => toggleSort("pe_relative_to_nifty")}>
                P/E ÷ NIFTY P/E {sortGlyph("pe_relative_to_nifty", sortKey, sortDir)}
              </th>
              <th className="clickable" onClick={() => toggleSort("vs_nifty_classification")}>
                vs NIFTY <FieldHelp term="index_valuation_vs_nifty" /> {sortGlyph("vs_nifty_classification", sortKey, sortDir)}
              </th>
              <th className="c">Chart</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const v = r.valuation;
              const isOpen = expanded === r.security_id;
              return (
                <Fragment key={r.security_id}>
                  <tr>
                    <td className="l">
                      <span className="tk tk-link" onClick={() => onSelectIndex(r.security_id)} title="Open instrument detail">
                        {r.display_ticker ?? r.security_id}
                      </span>
                    </td>
                    <td className="r mono">{v.available && v.pe !== null ? v.pe.toFixed(2) : "—"}</td>
                    <td title={v.note}>
                      {v.classification ? <span className={`valuation-chip ${v.classification}`}>{v.classification}</span>
                        : <span className="mut">{v.available ? "n too small" : "no source"}</span>}
                    </td>
                    <td className="r mono">{v.pe_relative_to_nifty !== null ? v.pe_relative_to_nifty.toFixed(2) : "—"}</td>
                    <td>
                      {v.vs_nifty_classification
                        ? <span className={`valuation-chip outline ${v.vs_nifty_classification}`}>{v.vs_nifty_classification}</span>
                        : <span className="mut">{r.security_id === "IDX:NIFTY" ? "is the benchmark" : "—"}</span>}
                    </td>
                    <td className="c">
                      {v.available && (
                        <button className="btn ghost sm" onClick={() => setExpanded(isOpen ? null : r.security_id)}>
                          {isOpen ? "▾ hide" : "▸ chart"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} style={{ padding: "10px 16px", background: "var(--surface-2)" }}>
                        <IndexValuationChartRow token={token} securityId={r.security_id} asOf={asOf} onAuthError={onAuthError} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="foot-note">
        {data.methodology_note} {data.composition_drift_note}
      </div>
    </div>
  );
}

/** Lazily fetches ONE index's full valuation (with its post-2021 P/E
 * series) on expand, matching StrategySimulatorPage's own expand-to-fetch
 * row pattern -- never embedding all 16 indices' ~1,300-point series in
 * the bulk cross-sector response above. */
function IndexValuationChartRow({
  token, securityId, asOf, onAuthError,
}: {
  token: string; securityId: string; asOf: string; onAuthError: (err: unknown) => boolean;
}) {
  const [detail, setDetail] = useState<IndexValuationDto | null>(null);
  const [cape, setCape] = useState<CapeDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchIndexValuationDetail(token, securityId, asOf), fetchIndexCapeDetail(token, securityId)])
      .then(([v, c]) => { setDetail(v); setCape(c); })
      .catch((err) => { if (!onAuthError(err)) setError(err instanceof ApiError ? err.message : "failed to load chart"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId, asOf]);

  if (error) return <div className="error">{error}</div>;
  if (!detail || !cape) return <div className="note">Loading charts…</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <div className="note" style={{ fontWeight: 700, marginBottom: 4 }}>Trailing P/E vs own history <FieldHelp term="nifty_valuation_band" /></div>
        <ValuationBandChart
          series={detail.pe_series}
          meanPe={detail.mean_pe} sdPe={detail.sd_pe}
          currentPe={detail.pe} currentDate={detail.as_of_date}
          classification={detail.classification}
        />
      </div>
      {cape.available && (cape.pre?.available || cape.post?.available) && (
        <div>
          <div className="note" style={{ fontWeight: 700, marginBottom: 4 }}>CAPE fair-value range <FieldHelp term="cape_fair_value" /></div>
          {cape.nominal_note && <div className="valuation-methodology-note">{cape.nominal_note}</div>}
          {cape.segmentation_note && <div className="valuation-methodology-note">{cape.segmentation_note}</div>}
          <CapeChart pre={cape.pre} post={cape.post} />
        </div>
      )}
    </div>
  );
}
